import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { ProgramacionMasivaDetalle } from 'src/app/domain/model/agenda/programacion-masiva';
import { pmAsesoresTablePreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * Componente Dumb: Tabla de asesores seleccionados
 *
 * Solo presenta datos y emite eventos.
 * NO tiene lógica de negocio ni servicios inyectados.
 *
 * Legacy: Si el asesor ya tiene cita creada (nmconsecutivoCita != null),
 * no se puede eliminar. Si no tiene cita, muestra confirmación.
 */
@Component({
  selector: 'app-pm-asesores-table',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    CardModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['../../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-confirmDialog></p-confirmDialog>

    @if (asesores.length > 0) {
      <p-card class="mb-4">
        <ng-template pTemplate="header">
          <div class="p-3 pb-0 flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center">
            <h3 class="text-base sm:text-lg font-semibold text-gray-700 m-0">
              Listado de Consultores
            </h3>
            <span class="text-xs sm:text-sm text-gray-500"
              >Registros del 1 hasta el {{ asesores.length }} de
              {{ asesores.length }}</span
            >
          </div>
        </ng-template>

        <div class="md:hidden space-y-3 p-3">
          @for (asesor of asesores; track $index) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Consultor
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(asesor.nombreAsesor)"
                >
                  {{ textoTabla(asesor.nombreAsesor) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Dni:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(asesor.dniAsesor)
                  }}</span>
                </div>
              </div>
              @if (!readonly && asesor.nmconsecutivoCita == null) {
                <button
                  pButton
                  icon="pi pi-trash"
                  label="Eliminar"
                  class="p-button-danger p-button-outlined p-button-sm min-w-[44px] min-h-[44px] w-full"
                  (click)="onEliminarClick(asesor)"
                ></button>
              }
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig.a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="asesores"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [tableStyle]="{
              'min-width': tableConfig.responsive.minWidth || 'max-content'
            }"
            [scrollHeight]="tableConfig.responsive.scrollHeight || '260px'"
            [styleClass]="tableStyleClass"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Dni">Dni</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Consultor"
                    >Consultor</span
                  >
                </th>
                <th pResizableColumn class="text-center">
                  <span class="grid-header-text" title="Eliminar"
                    >Eliminar</span
                  >
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-asesor>
              <tr>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(asesor.dniAsesor)"
                    >{{ textoTabla(asesor.dniAsesor) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(asesor.nombreAsesor)"
                    >{{ textoTabla(asesor.nombreAsesor) }}</span
                  >
                </td>
                <td class="text-center">
                  <!-- Legacy: Solo mostrar eliminar si NO tiene cita programada -->
                  @if (!readonly && asesor.nmconsecutivoCita == null) {
                    <button
                      pButton
                      icon="pi pi-trash"
                      class="p-button-rounded p-button-text p-button-sm p-button-danger min-w-[44px] min-h-[44px]"
                      pTooltip="Eliminar consultor"
                      (click)="onEliminarClick(asesor)"
                    ></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </p-card>
    }
  `
})
export class PmAsesoresTableComponent {
  @Input() asesores: ProgramacionMasivaDetalle[] = [];
  @Input() readonly = false;
  @Output() eliminar = new EventEmitter<ProgramacionMasivaDetalle>();

  private readonly confirmationService = inject(ConfirmationService);

  readonly tableConfig = pmAsesoresTablePreset.createConfig({
    style: {
      tableClassName: 'tabla-pm-asesores',
      striped: true,
      gridlines: false,
      resizableColumns: true,
      rowHover: false
    },
    responsive: {
      mode: 'hybrid',
      minWidth: 'max-content',
      scrollHeight: '260px',
      cardBreakpointPx: 768
    },
    a11y: {
      tableAriaLabel:
        'Tabla de consultores seleccionados en programacion masiva',
      announceSelection: false,
      announcePageChanges: false,
      focusFirstActionOnRowSelect: false
    }
  });

  get tableStyleClass(): string {
    const classes = ['p-datatable-sm', this.tableConfig.style.tableClassName];
    if (this.tableConfig.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (this.tableConfig.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  /**
   * Legacy: Muestra confirmación antes de eliminar
   * El botón solo aparece si nmconsecutivoCita == null (no programado)
   */
  onEliminarClick(asesor: ProgramacionMasivaDetalle): void {
    // Legacy: Utilidades.buildConfirmCustomDialog(...)
    this.confirmationService.confirm({
      message:
        '¿Está seguro de quitar el consultor de la lista de consultores a programar?',
      header: 'Confirmación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      accept: () => {
        this.eliminar.emit(asesor);
      }
    });
  }
}
